import React, { useEffect, useState } from 'react';
import { propertyAPI } from '../../services/api';
import { PropertyActionDialog } from './PropertyWorkspaceForms';
import {
  apiErrorMessage,
  INTEREST_OPTIONS,
  isConflict,
  labelFor,
  statusClass,
  workflowStatusLabel,
} from './propertyRegistry';
import {
  interestSnapshotKey,
  isPropertyChangeMaker,
  shortPartyUid,
} from './propertyInterestChangeUi';

const displayDateTime = value => value
  ? new Intl.DateTimeFormat('mr-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
  : '—';

const displayDate = value => value
  ? new Intl.DateTimeFormat('mr-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(`${value}T00:00:00`))
  : 'समाप्ती दिनांक नाही';

const InterestSnapshot = ({ title, subtitle, rows, tone }) => (
  <section className={`property-change-snapshot ${tone}`} aria-label={title}>
    <header><h4>{title}</h4><small>{subtitle}</small></header>
  {!rows?.length ? <p className="property-change-empty">मालकी हक्क नोंदवलेले नाहीत.</p> : (
      <div className="property-change-interest-list">
        {rows.map((row, index) => (
          <article key={interestSnapshotKey(row, index)}>
            <div>
              <strong title={row.party_uid}>{shortPartyUid(row.party_uid)}</strong>
              <small>{labelFor(INTEREST_OPTIONS, row.interest_type)}</small>
            </div>
          <div><strong>{row.ownership_share ?? '—'}%</strong><small>मालकी हिस्सा</small></div>
            <div>
              <span>{displayDate(row.valid_from)} – {displayDate(row.valid_to)}</span>
            <small>{row.is_primary ? 'प्राथमिक' : 'दुय्यम'} · {row.is_liable_party ? 'करदायी' : 'करदायी नाही'}</small>
            </div>
            {row.evidence?.note && <p>{row.evidence.note}</p>}
          </article>
        ))}
      </div>
    )}
  </section>
);

export default function PropertyInterestChangeRequests({
  tenantSlug,
  propertyUid,
  user,
  canVerify,
  onPropertyChanged,
}) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [review, setReview] = useState(null);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const response = await propertyAPI.getInterestChanges(tenantSlug, propertyUid, { page: 1, limit: 50 });
      setRequests(response.data.change_requests || []);
      setError('');
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'मालकी बदल विनंत्या लोड करता आल्या नाहीत.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRequests(); }, [tenantSlug, propertyUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async reason => {
    if (!review || busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const payload = { version: review.request.version, reason };
      if (review.decision === 'approve') {
        await propertyAPI.approveInterestChange(tenantSlug, propertyUid, review.request.request_uid, payload);
        await onPropertyChanged?.();
        setMessage('मालकी बदल मंजूर करून मालमत्तेला लागू केला.');
      } else {
        await propertyAPI.rejectInterestChange(tenantSlug, propertyUid, review.request.request_uid, payload);
        setMessage('मालकी बदल विनंती नामंजूर केली.');
      }
      setReview(null);
      await loadRequests();
    } catch (requestError) {
      const conflict = isConflict(requestError);
      const nextError = `${apiErrorMessage(requestError, 'मालकी बदलाची पडताळणी करता आली नाही.')}${conflict ? ' नवीनतम विनंती आणि मालमत्ता स्थिती पुन्हा लोड केली आहे.' : ''}`;
      setReview(null);
      if (conflict) {
        await Promise.all([loadRequests(), onPropertyChanged?.()]);
      }
      setError(nextError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="property-change-requests">
      {message && <div className="property-alert success" role="status">{message}</div>}
      {error && <div className="property-alert error" role="alert"><p>{error}</p><button type="button" onClick={loadRequests}>विनंत्या पुन्हा लोड करा</button></div>}
      {loading ? <div className="property-change-loading" role="status">मालकी बदल विनंत्या लोड होत आहेत…</div> : requests.length === 0 ? (
        <div className="property-empty-inline">या मालमत्तेसाठी मालकी बदल विनंती सादर केलेली नाही.</div>
      ) : requests.map(request => {
        const maker = isPropertyChangeMaker(request, user);
        const pending = request.status === 'pending';
        return (
          <article className="property-change-request" key={request.request_uid}>
            <header>
              <div>
                <span className={`property-status ${statusClass(request.status)}`}>{workflowStatusLabel(request.status)}</span>
                  <h3>संपूर्ण मालकी संच बदल</h3>
                <p className="property-monospace">{request.request_uid}</p>
              </div>
              <dl>
                <div><dt>प्रस्तावक</dt><dd>वापरकर्ता क्र. {request.proposed_by_user_id}</dd></div>
                <div><dt>प्रस्ताव दिनांक</dt><dd>{displayDateTime(request.proposed_at)}</dd></div>
                <div><dt>मूळ आवृत्ती</dt><dd>{request.base_resource_version}</dd></div>
              </dl>
            </header>
              <div className="property-change-reason"><strong>प्रस्तावाचे कारण</strong><p>{request.proposal_reason}</p></div>
            <div className="property-change-comparison">
              <InterestSnapshot title="सध्याची नोंद" subtitle="प्रस्तावाच्या वेळी जतन केलेली अपरिवर्तनीय प्रत" rows={request.baseline_set} tone="baseline" />
              <InterestSnapshot title="प्रस्तावित मालकी" subtitle="मंजुरीनंतर लागू होणारा संपूर्ण मालकी संच" rows={request.requested_set} tone="requested" />
            </div>
            {request.reviewed_at && (
              <div className="property-change-review-summary">
                वापरकर्ता क्र. {request.reviewed_by_user_id} यांनी {displayDateTime(request.reviewed_at)} रोजी तपासले
                {request.review_reason ? ` · ${request.review_reason}` : ''}
              </div>
            )}
            {pending && canVerify && (
              <footer>
                    <button type="button" className="property-secondary-button danger" disabled={maker || busy} onClick={() => setReview({ request, decision: 'reject' })}>नामंजूर करा</button>
                    <button type="button" className="property-primary-button success" disabled={maker || busy} onClick={() => setReview({ request, decision: 'approve' })}>मंजूर करून लागू करा</button>
                    {maker && <small>ही विनंती आपण सादर केली आहे. मालमत्ता पडताळणी परवानगी असलेल्या दुसऱ्या वापरकर्त्याने तिची तपासणी करणे आवश्यक आहे.</small>}
              </footer>
            )}
          </article>
        );
      })}
      {review && (
        <PropertyActionDialog
          title={review.decision === 'approve' ? 'मालकी बदल मंजूर करा' : 'मालकी बदल नामंजूर करा'}
          description={review.decision === 'approve'
            ? 'प्रस्तावित संपूर्ण मालकी संच एकाच व्यवहारात सध्याच्या नोंदीची जागा घेईल. तो अंशतः लागू करता येणार नाही.'
            : 'सध्याची मालकी नोंद बदलणार नाही. प्रस्तावकासाठी स्पष्ट पडताळणी कारण द्या.'}
          confirmLabel={review.decision === 'approve' ? 'मंजूर करून लागू करा' : 'विनंती नामंजूर करा'}
          reasonRequired
          busy={busy}
          onCancel={() => setReview(null)}
          onConfirm={decide}
        />
      )}
    </div>
  );
}
